import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase.js'
import { useAuth } from './useAuth.js'

// Permission levels and capabilities - ES2022+ frozen constants
const PERMISSION_LEVELS = Object.freeze({
  SYSADMIN: 'sysadmin',
  ADMIN: 'admin', 
  TECHNICIAN: 'technician',
  USER: 'user',
})

const PERMISSION_CONFIG = Object.freeze({
  [PERMISSION_LEVELS.SYSADMIN]: { 
    display: 'System Admin', 
    badge: 'bg-danger', 
    icon: 'bi-shield-check',
    canRead: true,
    canWrite: true,
    canManage: true,
  },
  [PERMISSION_LEVELS.ADMIN]: { 
    display: 'Admin', 
    badge: 'bg-primary', 
    icon: 'bi-person-gear',
    canRead: true,
    canWrite: true,
    canManage: true,
  },
  [PERMISSION_LEVELS.TECHNICIAN]: { 
    display: 'Technician', 
    badge: 'bg-warning', 
    icon: 'bi-tools',
    canRead: true,
    canWrite: true,
    canManage: false,
  },
  [PERMISSION_LEVELS.USER]: { 
    display: 'User', 
    badge: 'bg-success', 
    icon: 'bi-eye',
    canRead: true,
    canWrite: false,
    canManage: false,
  },
})

const DEFAULT_CONFIG = Object.freeze({
  display: 'No Access',
  badge: 'bg-secondary',
  icon: 'bi-x-circle',
  canRead: false,
  canWrite: false,
  canManage: false,
})

// State reducer for efficient permission management
const permissionReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true }
    case 'SET_PERMISSION':
      return {
        ...state,
        permission: action.permission,
        permissionData: action.permissionData,
        isSysAdmin: action.permission === PERMISSION_LEVELS.SYSADMIN,
        loading: false,
      }
    case 'NO_ACCESS':
      return {
        ...state,
        permission: null,
        permissionData: null,
        isSysAdmin: false,
        loading: false,
      }
    case 'RESET':
      return {
        permission: null,
        permissionData: null,
        isSysAdmin: false,
        loading: true,
      }
    default:
      return state
  }
}

/**
 * Device Permission Hook - Firebase v11+ with React 19+ optimizations
 * Real-time permission management for device access control
 */
export function useDevicePermission(deviceId) {
  const [state, dispatch] = useReducer(permissionReducer, {
    permission: null,
    permissionData: null,
    isSysAdmin: false,
    loading: true,
  })

  const { user } = useAuth()

  // Process Firestore timestamps
  const processTimestamps = useCallback((data) => ({
    ...data,
    addedAt: data.addedAt?.toDate?.() ?? data.addedAt,
    lastAccessedAt: data.lastAccessedAt?.toDate?.() ?? data.lastAccessedAt,
  }), [])

  // Permission subscription effect
  useEffect(() => {
    if (!user?.uid || !deviceId?.trim()) {
      dispatch({ type: 'NO_ACCESS' })
      return
    }

    dispatch({ type: 'LOADING' })

    const userRef = doc(db, `users/${user.uid}`)
    const deviceRef = doc(db, `userDevices/${user.uid}/devices/${deviceId}`)

    // Single unified subscription handler
    const handlePermissionUpdate = async () => {
      try {
        // Check system admin status first
        const userDoc = await getDoc(userRef)
        if (userDoc.exists() && userDoc.data().isSysAdmin === true) {
          dispatch({
            type: 'SET_PERMISSION',
            permission: PERMISSION_LEVELS.SYSADMIN,
            permissionData: {
              permissions: 'sysadmin',
              addedAt: new Date(),
              addedBy: 'system',
              source: 'sysadmin-global-access',
            },
          })
          return
        }

        // Check device-specific permissions for regular users
        const deviceDoc = await getDoc(deviceRef)
        if (deviceDoc.exists()) {
          const data = deviceDoc.data()
          dispatch({
            type: 'SET_PERMISSION',
            permission: data.permissions ?? PERMISSION_LEVELS.USER,
            permissionData: processTimestamps(data),
          })
        } else {
          dispatch({ type: 'NO_ACCESS' })
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('Permission update failed:', error)
        dispatch({ type: 'NO_ACCESS' })
      }
    }

    // Subscribe to both user and device changes
    const unsubscribeUser = onSnapshot(userRef, handlePermissionUpdate)
    const unsubscribeDevice = onSnapshot(deviceRef, handlePermissionUpdate)

    return () => {
      unsubscribeUser?.()
      unsubscribeDevice?.()
    }
  }, [user?.uid, deviceId, processTimestamps])

  // Memoized permission configuration
  const permissionConfig = useMemo(() => 
    PERMISSION_CONFIG[state.permission] ?? DEFAULT_CONFIG, 
    [state.permission],
  )

  // Return memoized computed properties
  return useMemo(() => ({
    permission: state.permission,
    loading: state.loading,
    isSysAdmin: state.isSysAdmin,
    permissionData: state.permissionData,
    canRead: permissionConfig.canRead,
    canWrite: permissionConfig.canWrite,
    canManage: permissionConfig.canManage,
    display: permissionConfig.display,
    style: {
      badge: permissionConfig.badge,
      icon: permissionConfig.icon,
    },
  }), [state, permissionConfig])
}
